<template>
	<div class="residential-menu-container">
		<ResidentialMenuNavbar
			:hideMenuNavigation="showComingSoon"
			:menus="menus"
			:menuIdInView="menuIdInView"
			@scrollToMenu="scrollToMenu"
		/>
		<ResidentialComingSoonSection v-if="showComingSoon" />
		<div class="navbar-page menu-page-container desktop-center" v-else>
			<div
				class="menu-page sk-scrollbar"
				v-if="!loading"
				@scroll="menuScrolled"
			>
				<div v-if="showMenu">
					<transition-group name="collapse" mode="out-in">
						<MenuSection
							v-for="menu in menus"
							ref="menuSections"
							:key="menu.id"
							:menu="menu"
						/>
					</transition-group>
				</div>
			</div>
			<FloatingCartSummary ref="cartSummary" />
			<div v-if="showMenu">
				<Footer @openCartSummary="openCartSummary" />
			</div>
		</div>
		<ItemModal ref="itemModal" />
	</div>
</template>

<style scoped>
.residential-menu-container >>> .desktop-center {
	max-width: 1300px !important;
}

.residential-menu-container {
	background: var(--sk-grey-bg);
	height: 100%;
}

.menu-page {
	position: relative;
	box-shadow: none;
}

@media (max-width: 1130px) {
	.navbar-page {
		height: calc(100% - 100px);
		min-height: calc(100% - 100px);
	}
}

@media (max-width: 900px) {
	.menu-page {
		padding-bottom: 70px;
	}
}
</style>

<script>
import utils from "@/utils"
import moment from "moment"
import momentUtils from "@/moment-utils"
import menuMixin from "@/mixins/menu-mixin"
import loaderMixin from "@/mixins/loader-mixin"
import ResidentialMenuNavbar from "@/components/navbars/ResidentialMenuNavbar.vue"
import Footer from "@/components/Footer.vue"
import ResidentialComingSoonSection from "@/components/common/ResidentialComingSoonSection.vue"
import MenuSection from "@/components/menu/MenuSection.vue"
import FloatingCartSummary from "@/components/common/FloatingCartSummary.vue"
import ItemModal from "@/components/modals/ItemModal.vue"

export default {
	name: "ResidentialMenu",
	mixins: [menuMixin, loaderMixin],
	components: {
		ResidentialMenuNavbar,
		ResidentialComingSoonSection,
		MenuSection,
		Footer,
		ItemModal,
		FloatingCartSummary
	},
	data: function () {
		return {
			menuIdInView: null
		}
	},
	computed: {
		menus() {
			if (
				!this.$store.state.deliveryDate ||
				!this.$store.getters.menusHash
			) {
				return []
			}
			const todaysDate = momentUtils.todaysDate()
			const selectedDate = moment(
				this.$store.state.deliveryDate.date,
				"YYYY-MM-DD"
			)
			const isToday = momentUtils.isSameDay(selectedDate, todaysDate)
			const offset =
				this.$store.state.business &&
				this.$store.state.business.timezone_offset_adjusted
					? this.$store.state.business.timezone_offset_adjusted
					: 0
			const now = moment.utc().add(offset, "m")

			const menu = {}
			this.menu.forEach((item) => {
				if (!menu[item.menu_id]) {
					const inStock =
						this.$store.getters.menusHash[item.menu_id].menu
							.stock_status == 1
					const menuSchedule = this.$store.state.menuSchedules[
						item.menu_id
					]
					const selectedDowSchedules =
						menuSchedule && menuSchedule[this.selectedDow]
							? menuSchedule[this.selectedDow]
							: []

					if (isToday) {
						const shouldBeInStock = selectedDowSchedules.some(
							(schedule) =>
								momentUtils.isBetweenTimes(
									now,
									schedule.openTime,
									schedule.closeTime
								)
						)
						if (shouldBeInStock && !inStock) {
							return
						}
					}

					const restaurant = this.$store.state.restaurants
						? this.$store.state.restaurants[item.restaurant_id]
						: null

					const itemMenu = this.$store.getters.menusHash[item.menu_id]
					menu[item.menu_id] = {
						id: item.menu_id,
						name: itemMenu.menu.name,
						categoryOrder: itemMenu.categoryOrder,
						restaurantName:
							restaurant && restaurant.name
								? restaurant.name
								: "",
						items: [],
						orderIndex: 0
					}

					let orderIndex = null
					selectedDowSchedules.forEach((schedule) => {
						const numOpenTime = Number(
							schedule.openTime.replace(":", "")
						)
						if (orderIndex == null || orderIndex < numOpenTime) {
							orderIndex = numOpenTime
						}
					})
					menu[item.menu_id].orderIndex = orderIndex
					menu[
						item.menu_id
					].timeSlots = momentUtils.generateTimeSlots(
						selectedDowSchedules,
						inStock,
						isToday
					)
				}
				if (menu[item.menu_id].orderIndex != null) {
					menu[item.menu_id].items.push(item)
				}
			})
			return Object.values(menu).sort(
				(a, b) => a.orderIndex - b.orderIndex
			)
		},
		showMenu() {
			return !this.showComingSoon
		},
		selectedDow() {
			if (this.$store.state.deliveryDate) {
				return momentUtils.normalizeDayOfWeek(
					moment(this.$store.state.deliveryDate.date, "YYYY-MM-DD")
				)
			}
			return null
		}
	},
	methods: {
		menuScrolled() {
			const sections = this.$refs.menuSections
				? this.$refs.menuSections
				: []
			let sectionIndexInView = sections.findIndex((section) => {
				let element = section.$el
				let inViewport = utils.isVisible(element, 100)
				return inViewport
			})
			this.menuIdInView = this.menus[sectionIndexInView]
				? this.menus[sectionIndexInView].id
				: null
		},
		scrollToMenu(menuId) {
			const menuIndex = this.menus.findIndex((menu) => menu.id == menuId)
			if (menuIndex > -1 && this.$refs.menuSections) {
				const menuSection = this.$refs.menuSections[menuIndex]
				if (menuSection && menuSection.$el) {
					menuSection.$el.scrollIntoView({
						block: "start",
						behavior: "smooth"
					})
				}
			}
		},
		openCartSummary() {
			this.$refs.cartSummary.open()
		}
	},
	mounted: function () {
		this.mounted = true
		if (this.menus.length > 0) {
			if (this.$route.query.scroll_to_menu) {
				this.scrollToMenu(this.$route.query.scroll_to_menu)
			}
		}
		else {
			this.$router.push({
					name: "home",
					params: { slug: this.$route.params.slug }
				})
		}
	}
}
</script>